import { DBSchema, IDBPDatabase, openDB } from 'idb';

import { RemoteClient, ZSetResponse } from '@/lib/storage/types';

import { KVMetricsTracker } from './kv-metrics-tracker';
import { BaseRangeOptions, KVValue } from './types';

// Define the type for our store record
export interface MockRemoteStoreRecord {
  key: string;
  value: unknown;
}

export interface MockRemoteStoreValue {
  setKey: string;
  member: string; // Always stored as string with type metadata
  score: number;
  timestamp: number;
}

export interface MockRemoteZsetRecord {
  key: [string, string]; // [setKey, stringified member]
  value: MockRemoteStoreValue;
  indexes: { 'by-set': string };
}

interface MockKVDatabase extends DBSchema {
  mockRemoteStore: {
    key: string;
    value: MockRemoteStoreRecord;
  };
  mockRemoteZSet: {
    key: [string, string];
    value: MockRemoteStoreValue;
    indexes: { 'by-set': string };
  };
}

export class MockVercelClient extends KVMetricsTracker implements RemoteClient {
  private db: IDBPDatabase<MockKVDatabase> | null = null;
  private initialized = false;

  constructor() {
    super();
  }

  public async ensureInitialized() {
    if (this.initialized && this.db) return;

    console.log('Initializing mock Vercel client storage...');

    this.db = await openDB<MockKVDatabase>('mock-vercel-kv', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('mockRemoteStore')) {
          db.createObjectStore('mockRemoteStore', { keyPath: 'key' });
        }

        if (!db.objectStoreNames.contains('mockRemoteZSet')) {
          const store = db.createObjectStore('mockRemoteZSet', {
            keyPath: ['setKey', 'member']
          });
          store.createIndex('by-set', 'setKey');
        }
      }
    });

    this.initialized = true;
    console.log('Mock Vercel client storage initialized');
  }

  private async simulateLatency() {
    const delay = Math.random() * 200 + 100; // 100-300ms delay
    await new Promise(resolve => setTimeout(resolve, delay));

    // Randomly fail some requests (1% chance)
    if (Math.random() < 0.01) {
      throw new Error('Mock network error');
    }
  }

  private serializeMember(member: unknown): string {
    if (member instanceof Date) {
      return `__DATE__${member.toISOString()}`;
    }
    if (member === undefined) {
      return '__UNDEFINED__';
    }
    if (member === null) {
      return '__NULL__';
    }
    return JSON.stringify(member);
  }

  private deserializeMember(stringMember: unknown): unknown {
    if (typeof stringMember !== 'string') {
      // If the input is not a string, return it directly
      return stringMember;
    }

    if (stringMember.startsWith('__DATE__')) {
      return new Date(stringMember.slice(8));
    }
    if (stringMember === '__UNDEFINED__') {
      return undefined;
    }
    if (stringMember === '__NULL__') {
      return null;
    }
    try {
      return JSON.parse(stringMember);
    } catch {
      // If parsing fails, return the original string
      return stringMember;
    }
  }

  async set(key: string, value: unknown): Promise<void> {
    this.trackOperation('set', key);
    await this.ensureInitialized();
    await this.simulateLatency();

    const serializedValue = this.serializeMember(value);
    await this.db?.put('mockRemoteStore', {
      key,
      value: serializedValue
    });
  }

  async get<T>(key: string): Promise<KVValue<T>> {
    this.trackOperation('get', key);
    await this.ensureInitialized();
    await this.simulateLatency();

    if (!this.db) {
      throw new Error('Database not initialized');
    }
    const result = await this.db.get('mockRemoteStore', key);
    if (!result) {
      return null;
    }
    return this.deserializeMember(result.value as string) as T;
  }

  async del(key: string): Promise<void> {
    this.trackOperation('del', key);
    await this.ensureInitialized();
    await this.simulateLatency();

    await this.db?.delete('mockRemoteStore', key);
  }

  async scan(cursor: number, options: { match?: string; count?: number } = {}): Promise<[number, string[]]> {
    this.trackOperation('scan', options.match ?? '*');
    await this.ensureInitialized();
    await this.simulateLatency();

    const tx = this.db?.transaction('mockRemoteStore', 'readonly');
    if (!tx) {
      throw new Error('Transaction could not be created');
    }
    const store = tx.store;
    const allKeys = await store.getAllKeys();

    // Filter keys by pattern if match option is provided
    const filteredKeys = options.match
      ? allKeys.filter(key => this.matchPattern(key.toString(), options.match!))
      : allKeys;

    const count = options.count ?? filteredKeys.length;
    const nextCursor = cursor + count >= filteredKeys.length ? 0 : cursor + count;
    const keys = filteredKeys.slice(cursor, cursor + count);

    return [nextCursor, keys.map(key => key.toString())];
  }

  private matchPattern(str: string, pattern: string): boolean {
    const regex = new RegExp('^' + pattern.replace(/\*/g, '.*').replace(/\?/g, '.') + '$');
    return regex.test(str);
  }

  async zadd(key: string, data: { score: number; member: unknown }): Promise<void> {
    this.trackOperation('zadd', key);
    await this.ensureInitialized();
    await this.simulateLatency();

    const stringMember = this.serializeMember(data.member);

    await this.db?.put('mockRemoteZSet', {
      setKey: key,
      member: stringMember,
      score: data.score,
      timestamp: Date.now()
    });
  }

  async zrem(key: string, member: unknown): Promise<void> {
    this.trackOperation('zrem', key);
    await this.ensureInitialized();
    await this.simulateLatency();

    const stringMember = this.serializeMember(member);
    await this.db?.delete('mockRemoteZSet', [key, stringMember]);
  }

  async zrangeByScore<T>(
    key: string,
    min: number | string,
    max: number | string,
    options: BaseRangeOptions = {}
  ): Promise<ZSetResponse<T>[]> {
    this.trackOperation('zrangeByScore', key);
    await this.ensureInitialized();
    await this.simulateLatency();

    const tx = this.db?.transaction('mockRemoteZSet', 'readonly');
    if (!tx) {
      throw new Error('Transaction could not be created');
    }

    const index = tx.store.index('by-set');
    const items = await index.getAll(key);

    // Convert string scores to numbers if needed
    const minScore = typeof min === 'string' ? parseFloat(min) : min;
    const maxScore = typeof max === 'string' ? parseFloat(max) : max;

    // Handle infinity strings
    const effectiveMin = min === '-inf' ? Number.NEGATIVE_INFINITY : minScore;
    const effectiveMax = max === '+inf' ? Number.POSITIVE_INFINITY : maxScore;

    // Filter items within score range
    const result = items.filter(item =>
      item.score >= effectiveMin && item.score <= effectiveMax
    );

    // Sort by score
    result.sort((a, b) => {
      const scoreDiff = a.score - b.score;
      return scoreDiff === 0 ? a.timestamp - b.timestamp : scoreDiff;
    });

    // Handle reverse option
    if (options.rev) {
      result.reverse();
    }

    // Return format depends on withScores option
    if (options.withScores) {
      return result.map(item => ({
        member: this.deserializeMember(item.member) as T,
        score: item.score
      }));
    }

    // Convert members back to their original types only
    return result.map(item => ({
      member: this.deserializeMember(item.member) as T
    }));
  }

  async zrangeByLex<T>(
    key: string,
    min: string,
    max: string,
    options: BaseRangeOptions = {}
  ): Promise<ZSetResponse<T>[]> {
    this.trackOperation('zrangeByLex', key);
    await this.ensureInitialized();
    await this.simulateLatency();

    const tx = this.db?.transaction('mockRemoteZSet', 'readonly');
    if (!tx) {
      throw new Error('Transaction could not be created');
    }

    const index = tx.store.index('by-set');
    const items = await index.getAll(key);

    // Helper to check if a value is within the lexicographical range
    const isInRange = (value: string) => {
      const minCheck = min === '-' || value >= min.replace(/[[(]/, '');
      const maxCheck = max === '+' || value <= max.replace(/[\])]/, '');

      // Handle inclusive/exclusive boundaries
      const isMinInclusive = min === '-' || min.startsWith('[');
      const isMaxInclusive = max === '+' || max.startsWith('[');

      if (!isMinInclusive && value === min.replace(/[[(]/, '')) return false;
      if (!isMaxInclusive && value === max.replace(/[\])]/, '')) return false;

      return minCheck && maxCheck;
    };

    // Filter items within lexicographical range
    const result = items.filter(item => {
      const memberStr = item.member.toString();
      return isInRange(memberStr);
    });

    // Sort lexicographically
    result.sort((a, b) => {
      const memberCompare = a.member.toString().localeCompare(b.member.toString());
      return memberCompare === 0 ? a.timestamp - b.timestamp : memberCompare;
    });

    return this.zrangeOutput<T>(result, options.withScores, options.rev);
  }

  private zrangeOutput<T>(result: MockRemoteStoreValue[], withScores: boolean | undefined, reverse: boolean | undefined): ZSetResponse<T>[] {
    // Handle reverse option
    if (reverse) {
      result.reverse();
    }

    // Return format depends on withScores option
    if (withScores) {
      return result.map(item => ({
        member: this.deserializeMember(item.member) as T,
        score: item.score
      }));
    }

    // Convert members back to their original types only
    return result.map(item => ({
      member: this.deserializeMember(item.member) as T
    }));
  }

  async zremrangebyscore(key: string, min: number, max: number): Promise<void> {
    this.trackOperation('zremrangebyscore', key);
    await this.ensureInitialized();
    await this.simulateLatency();

    const tx = this.db?.transaction('mockRemoteZSet', 'readwrite');
    if (!tx) {
      throw new Error('Transaction could not be created');
    }
    const index = tx.store.index('by-set');
    const items = await index.getAll(key);

    for (const item of items) {
      if (item.score >= min && item.score <= max) {
        await tx.store.delete([key, item.member]);
      }
    }
  }

  async zrange<T>(
  key: string,
  start: number,
  stop: number,
  options: BaseRangeOptions = {}
): Promise<ZSetResponse<T>[]> {
  this.trackOperation('zrange', key);
  await this.ensureInitialized();
  await this.simulateLatency();

  const tx = this.db?.transaction('mockRemoteZSet', 'readonly');
  if (!tx) {
    throw new Error('Transaction could not be created');
  }

  const index = tx.store.index('by-set');
  const items = await index.getAll(key);

  // Sort all items first by score, then by timestamp in case of ties
  items.sort((a, b) => {
    const scoreDiff = a.score - b.score;
    return scoreDiff === 0 ? a.timestamp - b.timestamp : scoreDiff;
  });

  // Handle negative indexes: convert them to positive offsets based on the length
  const totalItems = items.length;
  // Redis uses inclusive indices, so -1 means last element (length-1), -2 means second-to-last (length-2), etc.
  const effectiveStart = start < 0 ? totalItems + start : start;
  // For stop, we need to add 1 for slice's exclusive end, unless it's negative (already adjusted)
  const effectiveStop = stop < 0 ? totalItems + stop + 1 : stop + 1;

  // Slice the sorted array based on the start and stop indexes
  const result = items.slice(effectiveStart, effectiveStop);

  // Handle reverse option
  if (options.rev) {
    result.reverse();
  }

  // Return format depends on withScores option
  if (options.withScores) {
    return result.map(item => ({
      member: this.deserializeMember(item.member) as T,
      score: item.score
    }));
  }

  // Convert members back to their original types only
  return result.map(item => ({
    member: this.deserializeMember(item.member) as T
  }));
}
}

export async function createMockClient() {
  const client = new MockVercelClient();
  await client.ensureInitialized();
  return client;
}
